import { FC, useState, useMemo, useRef, useCallback } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
} from '@tanstack/react-table';
import { DndContext, closestCenter, DragMoveEvent } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DataTableSkeleton, Tag, Link as CarbonLink } from '@carbon/react';
import { useAtomValue } from 'jotai';
import { PageHeader } from 'components/page-header';
import { StatsBar, StatItem } from 'components/stats-bar';
import { TableActions } from 'components/table/table-actions';
import { DebouncedInput } from 'components/debounced-input';
import {
  DragHeader,
  DragCell,
  handleDragEnd,
  handleDragMove,
  getInitialColumnOrder,
} from 'components/table/draggable';
import { useColumnVisibility } from 'lib/hooks/useColumnVisibilityAtom';
import { useColumnState } from 'lib/hooks/useColumnState';
import { useColumnSizing } from 'lib/hooks/useColumnSizing';
import usePinnedColumnReordering from 'lib/hooks/usePinnedColumnReordering';
import { unitTypeMismatchesQueryLoadableAtom } from './atoms';
import type { UnitTypeMismatch } from './types';
import styles from './page.module.scss';

const columnHelper = createColumnHelper<UnitTypeMismatch>();

const columns = [
  columnHelper.accessor('id', {
    id: 'id',
    header: 'ID',
    size: 80,
  }),
  columnHelper.accessor('name', {
    id: 'name',
    header: 'Name',
    size: 200,
    cell: (info) => {
      const value = info.getValue();
      const row = info.row.original;
      return (
        <CarbonLink
          href={row.href}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {value}
        </CarbonLink>
      );
    },
  }),
  columnHelper.accessor('unitType', {
    id: 'unitType',
    header: 'Unit Type',
    size: 140,
    cell: (info) => {
      const value = info.getValue();
      return (
        <Tag type="red" size="sm">
          {value}
        </Tag>
      );
    },
  }),
  columnHelper.accessor('unitDevelopmentType', {
    id: 'unitDevelopmentType',
    header: 'Unit Development Type',
    size: 180,
    cell: (info) => {
      const value = info.getValue();
      return (
        <Tag type="blue" size="sm">
          {value}
        </Tag>
      );
    },
  }),
  columnHelper.accessor('updatedAt', {
    id: 'updatedAt',
    header: 'Updated At',
    size: 200,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleString() : '--';
    },
  }),
];

export const UnitTypeMismatchesQueryPage: FC = () => {
  const loadable = useAtomValue(unitTypeMismatchesQueryLoadableAtom);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const data = loadable.state === 'hasData' ? (loadable.data?.data ?? []) : [];
  const stats = loadable.state === 'hasData' ? loadable.data?.stats : null;

  const tableId = 'unit-type-mismatches-query-table';
  const initialColumnOrder = useMemo(() => getInitialColumnOrder(columns), []);

  const [columnVisibility, setColumnVisibility] = useColumnVisibility(tableId);
  const [columnSizing, setColumnSizing] = useColumnSizing(tableId);
  const { columnOrder, setColumnOrder, columnPinning, setColumnPinning } =
    useColumnState({
      tableId,
      initialColumnOrder,
      initialColumnPinning: { left: [] },
    });

  const hoveredColumnIdRef = useRef<string | null>(null);

  usePinnedColumnReordering(columnPinning, columnOrder, setColumnOrder);

  const onDragMove = useCallback(
    (e: DragMoveEvent) => {
      handleDragMove(e, hoveredColumnIdRef);
    },
    [hoveredColumnIdRef]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      columnVisibility,
      columnOrder,
      columnPinning,
      columnSizing,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: 'onChange',
    enableColumnPinning: true,
    enableColumnResizing: true,
  });

  const renderStatsBar = () => {
    if (!stats) return null;

    const statItems: StatItem[] = [
      { label: 'Total', value: stats.total, tagType: 'gray' },
    ];

    Object.entries(stats.byUnitType).forEach(
      ([type, count]: [string, number]) => {
        statItems.push({
          label: `Unit: ${type}`,
          value: count,
          tagType: 'red',
        });
      }
    );

    Object.entries(stats.byUnitDevelopmentType).forEach(
      ([type, count]: [string, number]) => {
        statItems.push({
          label: `Dev: ${type}`,
          value: count,
          tagType: 'blue',
        });
      }
    );

    return <StatsBar stats={statItems} />;
  };

  const renderContent = () => {
    if (loadable.state === 'loading') {
      return <DataTableSkeleton columnCount={5} rowCount={10} />;
    }

    if (loadable.state === 'hasError') {
      return (
        <div className={styles.error}>
          Error:{' '}
          {loadable.error instanceof Error
            ? loadable.error.message
            : 'Failed to fetch data'}
        </div>
      );
    }

    return (
      <>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionDescription}>
            Development areas where the <code>unit_type</code> field does not
            match the <code>unit_development_type</code> field.
          </div>
          <div className={styles.expectedState}>
            Expected: Empty - unit_type and unit_development_type should always
            match
          </div>
        </div>

        {renderStatsBar()}

        <div className={styles.stackTableArea}>
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={(e) =>
              handleDragEnd(
                e,
                columnPinning,
                columnOrder,
                setColumnOrder,
                setColumnPinning,
                hoveredColumnIdRef
              )
            }
            modifiers={[restrictToHorizontalAxis]}
            onDragMove={onDragMove}
          >
            <div className={styles.tableContext}>
              <DebouncedInput
                className={styles.searchField}
                value={globalFilter}
                onChange={(value) => setGlobalFilter(String(value))}
                placeholder="Search..."
              />
              <TableActions
                table={table}
                isColumnFilterActive={false}
                setIsColumnFilterActive={() => {}}
                hideColumnFilterButton
              />
            </div>

            <table className={styles.stackTable}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    <SortableContext
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      {headerGroup.headers.map((header) => (
                        <DragHeader
                          key={header.id}
                          header={header}
                          hoveredColumnIdRef={hoveredColumnIdRef}
                        />
                      ))}
                    </SortableContext>
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length}>
                      <div className={styles.emptyState}>
                        No unit type mismatches found
                      </div>
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      <SortableContext
                        items={columnOrder}
                        strategy={horizontalListSortingStrategy}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <DragCell
                            key={cell.id}
                            cell={cell}
                            hoveredColumnIdRef={hoveredColumnIdRef}
                          />
                        ))}
                      </SortableContext>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </DndContext>
        </div>
      </>
    );
  };

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Unit Type Mismatches"
        actions={null}
      />
      {renderContent()}
    </>
  );
};
